import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Award, Shield, Smartphone, Check, Search, FileText, ArrowRight, AlertCircle, Phone, Lock, Eye, EyeOff, RefreshCw, KeyRound } from 'lucide-react';

type LoginStep = 'phone' | 'vipps_waiting' | 'create_password' | 'enter_password' | 'forgot_password' | 'reset_code';

export function HomePage() {
  // Login flow state
  const [loginStep, setLoginStep] = useState<LoginStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetMethod, setResetMethod] = useState<'sms' | 'email'>('sms');

  // State from API
  const [profileId, setProfileId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Verification state
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Hvor skal bruker sendes etter vellykket login?
  // Ved 401 fra en intern side settes ?returnTo=/submissions/:id i URL-en;
  // vi lagrer den i sessionStorage slik at hele login-flyten (Vipps + evt.
  // passord/OTP) kan fullføres uten å miste målet.
  const RETURN_TO_KEY = 'vipps_return_to';
  const RETURN_TO_WHITELIST = /^\/(submissions\/[0-9a-f-]{36}|dashboard|cv-builder|learning-path|certificates\/[^/]+|external-credentials\/[^/]+|assessments|assessments\/[0-9a-f-]{36})$/;
  const resolveReturnTo = (): string => {
    try {
      const stored = sessionStorage.getItem(RETURN_TO_KEY);
      if (stored && RETURN_TO_WHITELIST.test(stored)) {
        sessionStorage.removeItem(RETURN_TO_KEY);
        return stored;
      }
    } catch { /* sessionStorage kan feile i private browsing */ }
    return '/dashboard';
  };

  // Check for error in URL params + fang opp returnTo
  useEffect(() => {
    const urlError = searchParams.get('error');
    const message = searchParams.get('message');
    const returnTo = searchParams.get('returnTo');
    if (returnTo && RETURN_TO_WHITELIST.test(returnTo)) {
      try { sessionStorage.setItem(RETURN_TO_KEY, returnTo); } catch { /* ignore */ }
    }
    if (urlError || message) {
      setError(message || urlError || 'En feil oppstod ved innlogging');
      setSearchParams({});
    } else if (returnTo) {
      // Rens URL (returnTo er lagret i sessionStorage nå)
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Load saved remember token from localStorage
  useEffect(() => {
    const savedRememberToken = localStorage.getItem('kandidat_remember_token');
    const savedProfileId = localStorage.getItem('kandidat_profile_id');
    if (savedRememberToken && savedProfileId) {
      setProfileId(savedProfileId);
      // Don't auto-login - user must still go through phone + Vipps
    }
  }, []);

  // Step 1: Check phone number
  const handleCheckPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim()) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/candidate/auth/check-phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });

      const data = await res.json();

      if (!data.exists && !data.allow_signup) {
        setError(data.message || 'Du er ikke registrert i systemet.');
        setLoading(false);
        return;
      }

      if (!data.exists && data.allow_signup) {
        // Ny bruker — fortsett til Vipps CIBA. findOrCreateProfile på backend
        // oppretter tom profil ved suksess. Onboarding-side vises etter login.
        setProfileId(null);
        setUserName('');
        await startCibaAuth();
        return;
      }

      // Phone exists - start CIBA. profile_id og full_name settes ETTER Vipps CIBA
      // (backend returnerer dem ikke lenger fra check-phone — sikkerhetsfiks mot
      // enumerering hvor angriper kunne slå opp offerets UUID via telefonnummer).
      setProfileId(null);
      setUserName('');

      // Check if we have a valid remember token
      const savedRememberToken = localStorage.getItem('kandidat_remember_token');
      if (savedRememberToken && data.has_remember_token) {
        // Will try to use remember token after Vipps verification
      }

      // Start CIBA authentication
      await startCibaAuth();

    } catch (err) {
      console.error('Check phone error:', err);
      setError('Kunne ikke sjekke telefonnummer. Prøv igjen.');
      setLoading(false);
    }
  };

  // Start CIBA authentication
  const startCibaAuth = async () => {
    setLoginStep('vipps_waiting');
    setError('');

    try {
      const res = await fetch('/api/candidate/auth/vipps/ciba-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber, remember_device: rememberMe })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Kunne ikke starte Vipps-autentisering');
        setLoginStep('phone');
        setLoading(false);
        return;
      }

      // Start polling for CIBA status
      startPolling(data.auth_req_id, data.poll_interval || 5);

    } catch (err) {
      console.error('CIBA start error:', err);
      setError('Kunne ikke starte Vipps-autentisering. Prøv igjen.');
      setLoginStep('phone');
      setLoading(false);
    }
  };

  // Poll for CIBA status
  const startPolling = (reqId: string, interval: number) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    const poll = async () => {
      try {
        const res = await fetch(`/api/candidate/auth/vipps/ciba-status/${reqId}`);
        const data = await res.json();

        if (data.status === 'pending') {
          // Still waiting for user to approve in Vipps app
          return;
        }

        if (data.status === 'vipps_verified') {
          // Vipps auth complete - stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }

          setSessionToken(data.session_token);
          setProfileId(data.profile_id);
          setUserName(data.user?.name || '');

          // Check if we can use remember token to skip password
          const savedRememberToken = localStorage.getItem('kandidat_remember_token');
          if (savedRememberToken && data.next_step === 'enter_password') {
            const rememberValid = await tryRememberToken(data.session_token, data.profile_id, savedRememberToken);
            if (rememberValid) {
              // Successfully logged in with remember token
              return;
            }
          }

          // Determine next step based on password status
          if (data.next_step === 'create_password') {
            setLoginStep('create_password');
          } else {
            setLoginStep('enter_password');
          }
          setLoading(false);
          return;
        }

        if (data.status === 'rejected' || data.status === 'expired' || data.status === 'error') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setError(data.error || 'Vipps-autentisering mislyktes');
          setLoginStep('phone');
          setLoading(false);
          return;
        }

      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Poll immediately, then at interval
    poll();
    pollingRef.current = setInterval(poll, interval * 1000);
  };

  // Try to use remember token to skip password
  const tryRememberToken = async (sessToken: string, profId: string, rememberToken: string): Promise<boolean> => {
    try {
      const res = await fetch('/api/candidate/auth/remember-token/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profId,
          remember_token: rememberToken,
          session_token: sessToken
        })
      });

      const data = await res.json();

      if (data.valid && data.authenticated) {
        // Successfully authenticated with remember token
        setLoading(false);
        navigate(resolveReturnTo());
        return true;
      }

      // Remember token invalid - clear it
      localStorage.removeItem('kandidat_remember_token');
      return false;

    } catch (err) {
      console.error('Remember token validation error:', err);
      return false;
    }
  };

  // Create password (first time)
  const handleCreatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !confirmPassword) return;

    if (password.length < 8) {
      setError('Passord må være minst 8 tegn');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passordene er ikke like');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/candidate/auth/password/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId,
          password: password,
          remember_me: rememberMe,
          session_token: sessionToken
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Kunne ikke opprette passord');
        setLoading(false);
        return;
      }

      // Save remember token if requested
      if (rememberMe && data.remember_token) {
        localStorage.setItem('kandidat_remember_token', data.remember_token);
        localStorage.setItem('kandidat_profile_id', profileId || '');
      }

      // Successfully authenticated
      navigate(resolveReturnTo());

    } catch (err) {
      console.error('Create password error:', err);
      setError('Kunne ikke opprette passord. Prøv igjen.');
      setLoading(false);
    }
  };

  // Verify password (returning user)
  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/candidate/auth/password/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId,
          password: password,
          remember_me: rememberMe,
          session_token: sessionToken
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Feil passord');
        setLoading(false);
        return;
      }

      // Save remember token if requested
      if (rememberMe && data.remember_token) {
        localStorage.setItem('kandidat_remember_token', data.remember_token);
        localStorage.setItem('kandidat_profile_id', profileId || '');
      }

      // Successfully authenticated
      navigate(resolveReturnTo());

    } catch (err) {
      console.error('Verify password error:', err);
      setError('Kunne ikke verifisere passord. Prøv igjen.');
      setLoading(false);
    }
  };

  // Request password reset
  const handleRequestReset = async (method: 'sms' | 'email') => {
    setLoading(true);
    setError('');
    setResetMethod(method);

    try {
      const res = await fetch('/api/candidate/auth/password/reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method: method,
          session_token: sessionToken
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Kunne ikke sende tilbakestillingskode');
        setLoading(false);
        return;
      }

      setLoginStep('reset_code');
      setLoading(false);

    } catch (err) {
      console.error('Request reset error:', err);
      setError('Kunne ikke sende tilbakestillingskode. Prøv igjen.');
      setLoading(false);
    }
  };

  // Verify reset code and set new password
  const handleVerifyReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode || !password || !confirmPassword) return;

    if (password.length < 8) {
      setError('Passord må være minst 8 tegn');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passordene er ikke like');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/candidate/auth/password/reset/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reset_code: resetCode,
          new_password: password,
          remember_me: rememberMe,
          session_token: sessionToken
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Ugyldig kode');
        setLoading(false);
        return;
      }

      // Save remember token if requested
      if (rememberMe && data.remember_token) {
        localStorage.setItem('kandidat_remember_token', data.remember_token);
        localStorage.setItem('kandidat_profile_id', profileId || '');
      }

      // Go back to enter password step
      setPassword('');
      setConfirmPassword('');
      setResetCode('');
      setLoginStep('enter_password');
      setLoading(false);
      setError('Passord tilbakestilt! Logg inn med ditt nye passord.');

    } catch (err) {
      console.error('Verify reset error:', err);
      setError('Kunne ikke tilbakestille passord. Prøv igjen.');
      setLoading(false);
    }
  };

  // Verify certificate/study plan
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) return;

    setVerifying(true);
    setVerifyError('');

    const code = verificationCode.trim();

    try {
      const certRes = await fetch(`/api/certificates/verify/${code}`);
      if (certRes.ok) {
        navigate(`/verify/${code}`);
        return;
      }
    } catch {
      // Continue to try study plan
    }

    try {
      const planRes = await fetch(`/verify-public/${code}`);
      if (planRes.ok) {
        window.location.href = `https://studieplan.kompetanseattest.no/verify/${code}`;
        return;
      }
    } catch {
      // Continue to error
    }

    setVerifyError('Koden er ugyldig eller dokumentet finnes ikke');
    setVerifying(false);
  };

  // Render login form based on current step
  const renderLoginForm = () => {
    switch (loginStep) {
      case 'phone':
        return (
          <form onSubmit={handleCheckPhone} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-2">
                Skriv inn mobilnummeret ditt
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s+]/g, ''))}
                  placeholder="F.eks. 91234567"
                  className="input w-full pl-10 text-lg"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-kf-error-50 border border-kf-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-kf-error-600 flex-shrink-0 mt-0.5" />
                <p className="text-kf-error-700 text-sm">{error}</p>
              </div>
            )}

            {/* Official Vipps Login Button - using official brand assets */}
            <button
              type="submit"
              disabled={loading || !phoneNumber.trim()}
              className="w-full h-[48px] rounded-[6px] font-medium text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[3px] hover:brightness-95 active:brightness-90"
              style={{ backgroundColor: '#FF5B24', color: 'white' }}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Venter...</span>
                </>
              ) : (
                <>
                  <span className="text-white font-medium">Logg inn med</span>
                  {/* Official Vipps logo (white) from VippsBrandKit-basic */}
                  <img
                    src="https://raw.githubusercontent.com/ScandinavianDesignGroup/VippsBrandKit-basic/master/1-vipps-logo/vipps-rgb-hvit.png"
                    alt="Vipps"
                    className="h-[38px]"
                  />
                </>
              )}
            </button>

            <p className="text-xs text-kf-gray-500 text-center">
              Du bekrefter innloggingen i Vipps-appen på telefonen din
            </p>
          </form>
        );

      case 'vipps_waiting':
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-kf-orange-100 rounded-full flex items-center justify-center mx-auto animate-pulse">
              <Smartphone className="w-10 h-10 text-kf-orange-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-kf-gray-900 mb-2">
                Bekreft i Vipps
              </h3>
              <p className="text-kf-gray-600">
                Åpne Vipps-appen på telefonen din og godkjenn innloggingen.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-kf-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Venter på bekreftelse...</span>
            </div>

            {error && (
              <div className="p-3 bg-kf-error-50 border border-kf-error-200 rounded-lg">
                <p className="text-kf-error-700 text-sm">{error}</p>
              </div>
            )}

            <button
              onClick={() => {
                if (pollingRef.current) {
                  clearInterval(pollingRef.current);
                  pollingRef.current = null;
                }
                setLoginStep('phone');
                setLoading(false);
                setError('');
              }}
              className="text-kf-gray-600 hover:text-kf-gray-900 text-sm underline"
            >
              Avbryt
            </button>
          </div>
        );

      case 'create_password':
        return (
          <form onSubmit={handleCreatePassword} className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-kf-gray-600">
                Hei, <span className="font-semibold">{userName}</span>!
              </p>
              <p className="text-sm text-kf-gray-500">
                Opprett et passord for enklere innlogging neste gang.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Velg passord
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minst 8 tegn"
                  className="input w-full pl-10 pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-kf-gray-400 hover:text-kf-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Bekreft passord
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Skriv passordet igjen"
                  className="input w-full pl-10"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-kf-gray-300 text-kf-orange-600 focus:ring-kf-orange-500"
              />
              <span className="text-sm text-kf-gray-600">Husk meg på denne enheten</span>
            </label>

            {error && (
              <div className="p-3 bg-kf-error-50 border border-kf-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-kf-error-600 flex-shrink-0 mt-0.5" />
                <p className="text-kf-error-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Oppretter...
                </span>
              ) : (
                'Opprett passord og logg inn'
              )}
            </button>
          </form>
        );

      case 'enter_password':
        return (
          <form onSubmit={handleVerifyPassword} className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-kf-gray-600">
                Velkommen tilbake, <span className="font-semibold">{userName}</span>!
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Passord
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Skriv inn passord"
                  className="input w-full pl-10 pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-kf-gray-400 hover:text-kf-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-kf-gray-300 text-kf-orange-600 focus:ring-kf-orange-500"
              />
              <span className="text-sm text-kf-gray-600">Husk meg på denne enheten</span>
            </label>

            {error && (
              <div className="p-3 bg-kf-error-50 border border-kf-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-kf-error-600 flex-shrink-0 mt-0.5" />
                <p className="text-kf-error-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Logger inn...
                </span>
              ) : (
                'Logg inn'
              )}
            </button>

            <button
              type="button"
              onClick={() => setLoginStep('forgot_password')}
              className="w-full text-center text-sm text-kf-gray-600 hover:text-kf-orange-600"
            >
              Glemt passord?
            </button>
          </form>
        );

      case 'forgot_password':
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <KeyRound className="w-12 h-12 text-kf-orange-600 mx-auto mb-2" />
              <h3 className="text-xl font-bold text-kf-gray-900">Tilbakestill passord</h3>
              <p className="text-sm text-kf-gray-600">
                Velg hvordan du vil motta tilbakestillingskoden
              </p>
            </div>

            <button
              onClick={() => handleRequestReset('sms')}
              disabled={loading}
              className="w-full p-4 border border-kf-gray-200 rounded-lg hover:border-kf-orange-300 hover:bg-kf-orange-50 transition-colors flex items-center gap-3"
            >
              <Smartphone className="w-6 h-6 text-kf-orange-600" />
              <div className="text-left">
                <p className="font-medium text-kf-gray-900">Send SMS</p>
                <p className="text-sm text-kf-gray-500">Få kode på mobilnummeret ditt</p>
              </div>
            </button>

            <button
              onClick={() => handleRequestReset('email')}
              disabled={loading}
              className="w-full p-4 border border-kf-gray-200 rounded-lg hover:border-kf-orange-300 hover:bg-kf-orange-50 transition-colors flex items-center gap-3"
            >
              <svg className="w-6 h-6 text-kf-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div className="text-left">
                <p className="font-medium text-kf-gray-900">Send e-post</p>
                <p className="text-sm text-kf-gray-500">Få kode på e-postadressen din</p>
              </div>
            </button>

            {error && (
              <div className="p-3 bg-kf-error-50 border border-kf-error-200 rounded-lg">
                <p className="text-kf-error-700 text-sm">{error}</p>
              </div>
            )}

            <button
              onClick={() => {
                setLoginStep('enter_password');
                setError('');
              }}
              className="w-full text-center text-sm text-kf-gray-600 hover:text-kf-gray-900"
            >
              Tilbake til innlogging
            </button>
          </div>
        );

      case 'reset_code':
        return (
          <form onSubmit={handleVerifyReset} className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-kf-gray-600">
                Vi har sendt en kode via {resetMethod === 'sms' ? 'SMS' : 'e-post'}.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Tilbakestillingskode
              </label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="Skriv inn koden"
                className="input w-full text-center text-lg tracking-widest"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Nytt passord
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minst 8 tegn"
                  className="input w-full pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-kf-gray-400 hover:text-kf-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Bekreft nytt passord
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Skriv passordet igjen"
                className="input w-full"
              />
            </div>

            {error && (
              <div className="p-3 bg-kf-error-50 border border-kf-error-200 rounded-lg">
                <p className="text-kf-error-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !resetCode || !password || !confirmPassword}
              className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Tilbakestiller...
                </span>
              ) : (
                'Tilbakestill passord'
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setLoginStep('forgot_password');
                setResetCode('');
                setPassword('');
                setConfirmPassword('');
                setError('');
              }}
              className="w-full text-center text-sm text-kf-gray-600 hover:text-kf-gray-900"
            >
              Send kode på nytt
            </button>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-12">
      {/* Hero Section with Two Columns */}
      <section className="grid md:grid-cols-2 gap-8 py-8">
        {/* Left: Login */}
        <div className="card bg-gradient-to-br from-kf-orange-50 to-white border-kf-orange-200">
          <div className="text-center">
            <div className="w-20 h-20 bg-kf-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-kf-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-kf-gray-900 mb-3">
              Mine Kompetanseattester
            </h2>
            <p className="text-kf-gray-600 mb-6">
              Logg inn for å se dine attester, laste ned dokumenter, og bygge din CV.
            </p>

            {renderLoginForm()}
          </div>
        </div>

        {/* Right: Verify document */}
        <div className="card bg-gradient-to-br from-kf-blue-50 to-white border-kf-blue-200">
          <div className="text-center">
            <div className="w-20 h-20 bg-kf-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-kf-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-kf-gray-900 mb-3">
              Verifiser Dokument
            </h2>
            <p className="text-kf-gray-600 mb-6">
              Kontroller gyldigheten til en kompetanseattest eller studieplan.
            </p>
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value);
                    setVerifyError('');
                  }}
                  placeholder="Skriv inn verifiseringskode"
                  className="input w-full text-center text-lg py-3"
                />
              </div>
              {verifyError && (
                <p className="text-kf-error-600 text-sm">{verifyError}</p>
              )}
              <button
                type="submit"
                disabled={verifying || !verificationCode.trim()}
                className="btn-secondary w-full sm:w-auto inline-flex items-center justify-center gap-2 py-3 px-8 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-kf-orange-600"></div>
                    Verifiserer...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    Verifiser
                  </>
                )}
              </button>
            </form>
            <p className="text-sm text-kf-gray-500 mt-4">
              Koden finner du på attesten eller studieplanen
            </p>
          </div>
        </div>
      </section>

      {/* What can you verify */}
      <section className="card bg-kf-gray-50">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-6 text-center">
          Hva kan verifiseres?
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-kf-orange-100 rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-kf-orange-600" />
            </div>
            <div>
              <h3 className="font-bold text-kf-gray-900 mb-1">Kompetanseattester</h3>
              <p className="text-sm text-kf-gray-600">
                Bevis på bestått eksamen med karakter, dato og utsteder. Signert digitalt.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-kf-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-kf-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-kf-gray-900 mb-1">Studieplaner</h3>
              <p className="text-sm text-kf-gray-600">
                Godkjente studieplaner med læringsutbytter, Kompetansepoeng (ECTS-skala) og vurderingsmetoder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="hva-er-kompetanseattest" className="grid md:grid-cols-3 gap-8">
        <div className="card text-center">
          <div className="w-16 h-16 bg-kf-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-kf-orange-600" />
          </div>
          <h3 className="text-xl font-bold text-kf-gray-900 mb-2">
            Digitalt Signert
          </h3>
          <p className="text-kf-gray-600">
            Hver attest er kryptografisk signert og kan verifiseres av
            arbeidsgivere og utdanningsinstitusjoner.
          </p>
        </div>

        <div className="card text-center">
          <div className="w-16 h-16 bg-kf-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-8 h-8 text-kf-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-kf-gray-900 mb-2">
            Alltid Tilgjengelig
          </h3>
          <p className="text-kf-gray-600">
            Tilgang fra mobil, nettbrett eller PC. Last ned PDF, legg til i Apple
            Wallet, eller eksporter til Europass.
          </p>
        </div>

        <div className="card text-center">
          <div className="w-16 h-16 bg-kf-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-kf-success-600" />
          </div>
          <h3 className="text-xl font-bold text-kf-gray-900 mb-2">
            Internasjonalt Anerkjent
          </h3>
          <p className="text-kf-gray-600">
            Følger EU Micro-credentials Framework og ELMv3-standarder for
            anerkjennelse i hele Europa.
          </p>
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-white rounded-lg p-8 border border-kf-gray-200">
        <h2 className="text-3xl font-bold text-kf-gray-900 mb-8 text-center">
          Hvordan fungerer det?
        </h2>

        <div className="space-y-6 max-w-3xl mx-auto">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-kf-orange-600 text-white rounded-full flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h4 className="font-bold text-kf-gray-900 mb-1">
                Fullfør eksamen
              </h4>
              <p className="text-kf-gray-600">
                Etter at du har fullført og levert din eksamen, registrerer du
                mobilnummer og e-postadresse.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-kf-orange-600 text-white rounded-full flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h4 className="font-bold text-kf-gray-900 mb-1">Motta varsel</h4>
              <p className="text-kf-gray-600">
                Når eksamen er sensurert, mottar du e-post og SMS med en lenke
                for å hente din kompetanseattest.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-kf-orange-600 text-white rounded-full flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h4 className="font-bold text-kf-gray-900 mb-1">
                Logg inn med Vipps
              </h4>
              <p className="text-kf-gray-600">
                Bekreft din identitet med Vipps for å sikre at attesten utstedes
                til riktig person.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-kf-orange-600 text-white rounded-full flex items-center justify-center font-bold">
              4
            </div>
            <div>
              <h4 className="font-bold text-kf-gray-900 mb-1">Hent attest</h4>
              <p className="text-kf-gray-600">
                Din digitale kompetanseattest er klar! Last ned PDF, del med
                arbeidsgivere, eller legg til i lommebok-appen din.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-kf-orange-500 to-kf-orange-700 rounded-lg p-12 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">Klar til å hente din attest?</h2>
        <p className="text-lg mb-8 opacity-90">
          Hvis du har mottatt en e-post eller SMS med en lenke, klikk på den for
          å komme i gang. Eller logg inn direkte for å se alle dine attester.
        </p>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-2 bg-white text-kf-orange-600 font-bold py-3 px-8 rounded-lg hover:bg-kf-gray-100 transition-colors"
        >
          <Award className="w-5 h-5" />
          Logg inn nå
          <ArrowRight className="w-5 h-5" />
        </button>
      </section>

      {/* FAQ */}
      <section>
        <h2 className="text-3xl font-bold text-kf-gray-900 mb-8 text-center">
          Ofte stilte spørsmål
        </h2>

        <div className="space-y-4 max-w-3xl mx-auto">
          <details className="card group">
            <summary className="font-bold text-kf-gray-900 cursor-pointer list-none flex justify-between items-center">
              Hva er en kompetanseattest?
              <span className="text-kf-orange-600 text-xl">+</span>
            </summary>
            <p className="mt-4 text-kf-gray-600">
              En kompetanseattest er et offisielt bevis på at du har fullført og
              bestått en kompetansevurdering. Den dokumenterer dine ferdigheter og
              kunnskaper innen et spesifikt område.
            </p>
          </details>

          <details className="card group">
            <summary className="font-bold text-kf-gray-900 cursor-pointer list-none flex justify-between items-center">
              Hvordan verifiserer arbeidsgivere attesten?
              <span className="text-kf-orange-600 text-xl">+</span>
            </summary>
            <p className="mt-4 text-kf-gray-600">
              Hver attest har en unik QR-kode og verifikasjonsnummer.
              Arbeidsgivere kan skanne QR-koden eller besøke
              verifiseringssiden for å bekrefte at attesten er ekte.
            </p>
          </details>

          <details className="card group">
            <summary className="font-bold text-kf-gray-900 cursor-pointer list-none flex justify-between items-center">
              Hvor lenge er attesten gyldig?
              <span className="text-kf-orange-600 text-xl">+</span>
            </summary>
            <p className="mt-4 text-kf-gray-600">
              Gyldighetsperioden varierer avhengig av type kompetanse. Noen
              attester er permanente, mens andre kan ha en begrenset
              gyldighetsperiode. Dette vil fremgå tydelig av attesten.
            </p>
          </details>

          <details className="card group">
            <summary className="font-bold text-kf-gray-900 cursor-pointer list-none flex justify-between items-center">
              Hva skjer hvis jeg mister tilgangen?
              <span className="text-kf-orange-600 text-xl">+</span>
            </summary>
            <p className="mt-4 text-kf-gray-600">
              Du kan alltid logge inn med telefonnummer og Vipps for å få tilgang til alle dine
              attester. Attesten er knyttet til din identitet, så du vil
              alltid ha tilgang.
            </p>
          </details>
        </div>
      </section>
    </div>
  );
}
